import React, { useState } from 'react';
import {
  Grid,
  TextField,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Chip,
  Button,
  Divider,
  FormHelperText,
} from '@mui/material';
import {
  Add as AddIcon,
} from '@mui/icons-material';
import { ListingForm as ListingFormType } from '@/types';

interface RulesPoliciesTabProps {
  formData: ListingFormType;
  errors: Record<string, string>;
  onFormDataChange: (updates: Partial<ListingFormType>) => void;
  onErrorsChange: (errors: Record<string, string>) => void;
  clearErrors: (fields: string[]) => void;
}

const CANCELLATION_POLICIES = [
  { value: 'flexible', label: 'Flexible', description: 'Full refund 1 day prior to arrival' },
  { value: 'moderate', label: 'Moderate', description: 'Full refund 5 days prior to arrival' },
  { value: 'strict', label: 'Strict', description: 'Full refund 14 days prior to arrival' },
  { value: 'super_strict_30', label: 'Super Strict 30', description: 'Full refund 30 days prior to arrival' },
  { value: 'super_strict_60', label: 'Super Strict 60', description: 'Full refund 60 days prior to arrival' },
];

const PET_POLICIES = [
  { value: 'not_allowed', label: 'Not allowed' },
  { value: 'allowed', label: 'Allowed' },
  { value: 'allowed_with_fee', label: 'Allowed with fee' },
];

const SMOKING_POLICIES = [
  { value: 'not_allowed', label: 'Not allowed' },
  { value: 'allowed_outside', label: 'Allowed outside only' },
  { value: 'allowed', label: 'Allowed' },
];

export const RulesPoliciesTab: React.FC<RulesPoliciesTabProps> = ({
  formData,
  errors,
  onFormDataChange,
  clearErrors,
}) => {
  const [newHouseRule, setNewHouseRule] = useState('');
  const [newAdditionalRule, setNewAdditionalRule] = useState('');

  const handleInputChange = (field: string, value: any) => {
    onFormDataChange({ [field]: value });
    clearErrors([field]);
  };

  const handleAdditionalInfoChange = (field: string, value: any) => {
    onFormDataChange({
      additionalInfo: {
        ...formData.additionalInfo,
        [field]: value,
      },
    });
    clearErrors([`additionalInfo.${field}`]);
  };

  const handleAddHouseRule = () => {
    if (newHouseRule.trim()) {
      const currentRules = formData.houseRules || [];
      onFormDataChange({
        houseRules: [...currentRules, newHouseRule.trim()],
      });
      setNewHouseRule('');
    }
  };

  const handleRemoveHouseRule = (index: number) => {
    const currentRules = formData.houseRules || [];
    onFormDataChange({
      houseRules: currentRules.filter((_, i) => i !== index),
    });
  };

  const handleAddAdditionalRule = () => {
    if (newAdditionalRule.trim()) {
      const currentRules = formData.additionalInfo?.additionalRules || [];
      handleAdditionalInfoChange('additionalRules', [...currentRules, newAdditionalRule.trim()]);
      setNewAdditionalRule('');
    }
  };

  const handleRemoveAdditionalRule = (index: number) => {
    const currentRules = formData.additionalInfo?.additionalRules || [];
    handleAdditionalInfoChange('additionalRules', currentRules.filter((_, i) => i !== index));
  };



  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Rules & Policies
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Set clear expectations for your guests with check-in/out times, house rules, and policies.
      </Typography>

      <Grid container spacing={3}>
        {/* Check-in/Check-out */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            Check-in & Check-out
          </Typography>
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            type="time"
            label="Check-in Time"
            value={formData.checkInTime || '15:00'}
            onChange={(e) => handleInputChange('checkInTime', e.target.value)}
            error={!!errors.checkInTime}
            helperText={errors.checkInTime || 'When guests can arrive'}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            type="time"
            label="Check-out Time"
            value={formData.checkOutTime || '11:00'}
            onChange={(e) => handleInputChange('checkOutTime', e.target.value)}
            error={!!errors.checkOutTime}
            helperText={errors.checkOutTime || 'When guests must leave'}
            InputLabelProps={{ shrink: true }}
          />
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>
            Stay Requirements
          </Typography>
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            type="number"
            label="Minimum Stay (nights)"
            value={formData.minimumStay || 1}
            onChange={(e) => handleInputChange('minimumStay', parseInt(e.target.value) || 1)}
            error={!!errors.minimumStay}
            helperText={errors.minimumStay || 'Minimum booking length'}
            inputProps={{ min: 1, max: 365 }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            fullWidth
            type="number"
            label="Maximum Stay (nights)"
            value={formData.maximumStay || 365}
            onChange={(e) => handleInputChange('maximumStay', parseInt(e.target.value) || 365)}
            helperText="Maximum booking length"
            inputProps={{ min: 1, max: 365 }}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.instantBook || false}
                onChange={(e) => handleInputChange('instantBook', e.target.checked)}
              />
            }
            label="Instant Book"
          />
          <FormHelperText>
            Allow guests to book immediately without approval
          </FormHelperText>
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>
            House Rules
          </Typography>
          <FormHelperText sx={{ mb: 2 }}>
            Set clear expectations for guest behavior during their stay.
          </FormHelperText>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              label="Add House Rule"
              value={newHouseRule}
              onChange={(e) => setNewHouseRule(e.target.value)}
              placeholder="e.g., No smoking inside"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddHouseRule();
                }
              }}
            />
            <Button
              variant="outlined"
              onClick={handleAddHouseRule}
              startIcon={<AddIcon />}
              disabled={!newHouseRule.trim()}
            >
              Add
            </Button>
          </Box>

          {formData.houseRules && formData.houseRules.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {formData.houseRules.map((rule, index) => (
                <Chip
                  key={index}
                  label={rule}
                  onDelete={() => handleRemoveHouseRule(index)}
                  color="primary"
                  variant="outlined"
                />
              ))}
            </Box>
          )}
        </Grid>

        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>
            Policies
          </Typography>
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControl fullWidth error={!!errors.cancellationPolicy}>
            <InputLabel>Cancellation Policy</InputLabel>
            <Select
              value={formData.cancellationPolicy || 'moderate'}
              onChange={(e) => handleInputChange('cancellationPolicy', e.target.value)}
              label="Cancellation Policy"
            >
              {CANCELLATION_POLICIES.map((policy) => (
                <MenuItem key={policy.value} value={policy.value}>
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {policy.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {policy.description}
                    </Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
            {errors.cancellationPolicy && (
              <FormHelperText>{errors.cancellationPolicy}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Pet Policy</InputLabel>
            <Select
              value={formData.additionalInfo?.petPolicy || 'not_allowed'}
              onChange={(e) => handleAdditionalInfoChange('petPolicy', e.target.value)}
              label="Pet Policy"
            >
              {PET_POLICIES.map((policy) => (
                <MenuItem key={policy.value} value={policy.value}>
                  {policy.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <FormControl fullWidth>
            <InputLabel>Smoking Policy</InputLabel>
            <Select
              value={formData.additionalInfo?.smokingPolicy || 'not_allowed'}
              onChange={(e) => handleAdditionalInfoChange('smokingPolicy', e.target.value)}
              label="Smoking Policy"
            >
              {SMOKING_POLICIES.map((policy) => (
                <MenuItem key={policy.value} value={policy.value}>
                  {policy.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.additionalInfo?.partiesAllowed || false}
                onChange={(e) => handleAdditionalInfoChange('partiesAllowed', e.target.checked)}
              />
            }
            label="Parties/Events Allowed"
          />
        </Grid>

        {/* Additional Rules */}
        <Grid item xs={12}>
          <Divider sx={{ my: 2 }} />
          <Typography variant="subtitle1" gutterBottom>
            Additional Rules
          </Typography>
          <FormHelperText sx={{ mb: 2 }}>
            Add any other specific rules or requirements for your property.
          </FormHelperText>
        </Grid>

        <Grid item xs={12}>
          <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
            <TextField
              fullWidth
              label="Add Additional Rule"
              value={newAdditionalRule}
              onChange={(e) => setNewAdditionalRule(e.target.value)}
              placeholder="e.g., Quiet hours after 10 PM"
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleAddAdditionalRule();
                }
              }}
            />
            <Button
              variant="outlined"
              onClick={handleAddAdditionalRule}
              startIcon={<AddIcon />}
              disabled={!newAdditionalRule.trim()}
            >
              Add
            </Button>
          </Box>

          {formData.additionalInfo?.additionalRules && formData.additionalInfo.additionalRules.length > 0 && (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {formData.additionalInfo.additionalRules.map((rule, index) => (
                <Chip
                  key={index}
                  label={rule}
                  onDelete={() => handleRemoveAdditionalRule(index)}
                  color="secondary"
                  variant="outlined"
                />
              ))}
            </Box>
          )}
        </Grid>
      </Grid>


    </Box>
  );
};

export default RulesPoliciesTab;